import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import Dashboard from "./pages/Dashboard";
import ConsultantDetail from "./pages/ConsultantDetail";
import DataSource from "./pages/DataSource";
import Settings from "./pages/Settings";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import ManagerDashboard from "./pages/ManagerDashboard";
import DirectorProjectManagement from "./pages/DirectorProjectManagement";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import IntelligenceDashboard from "./pages/IntelligenceDashboard";
import ReportBuilder from "./pages/ReportBuilder";
import Analytics from "./pages/Analytics";
import Layout from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import MyTeamDashboard from "./pages/MyTeamDashboard";
import ResetPassword from "./pages/ResetPassword";
import { Toaster } from "./components/ui/sonner";
import { MsalProvider } from "./context/MsalContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { VisibilityOverrideProvider } from "./context/VisibilityOverrideContext";
import "./App.css";

function ProtectedRoute({ children, minRole = "employee" }) {
  const { isAuthenticated, role, loading, needs2FASetup } = useAuth();
  
  const ROLE_LEVELS = { employee: 1, user: 1, manager: 2, admin: 3, director: 4 };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Mandatory 2FA: redirect to setup if not completed
  if (needs2FASetup) {
    return <Navigate to="/setup-2fa" replace />;
  }
  
  const userLevel = ROLE_LEVELS[role] || 1;
  const requiredLevel = ROLE_LEVELS[minRole] || 1;
  
  if (userLevel < requiredLevel) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function RoleLandingPage() {
  const { role } = useAuth();
  
  if (role === "employee" || role === "user") {
    return <EmployeeDashboard />;
  }
  if (role === "manager") {
    return <ManagerDashboard />;
  }
  // director and admin get the main dashboard
  return <Dashboard />;
}

function TwoFASetupPage() {
  const navigate = useNavigate();
  const { complete2FASetup, logout } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch QR code on mount
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/setup`)
      .then(res => setQrData(res.data))
      .catch(() => setError("Failed to load 2FA setup. Please try again."));
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/verify`, { code: totpCode });
      complete2FASetup();
      navigate("/");
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code. Try again.");
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center justify-center mb-6 bg-card rounded-xl shadow-sm py-5 border border-border">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center overflow-hidden mb-2">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/logo`} alt="SpillConsult" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">SpillConsult Analytics</h1>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Secured Business Intelligence</p>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 pt-6 pb-3">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 rounded-lg bg-amber-500/10"><span className="text-amber-500 text-lg">🔐</span></div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Two-Factor Authentication Required</h2>
                <p className="text-sm text-muted-foreground">2FA is mandatory. Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6">
            <form onSubmit={handleVerify} className="space-y-4">
              {qrData ? (
                <div className="flex flex-col items-center gap-3">
                  <img src={qrData.qr_code} alt="QR Code" className="w-48 h-48 rounded-lg border border-border" />
                  <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this key manually:</p>
                  <code className="text-xs bg-muted px-3 py-1.5 rounded font-mono select-all">{qrData.secret}</code>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48">
                  {error ? <p className="text-sm text-destructive">{error}</p> : <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />}
                </div>
              )}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Enter the 6-digit code from your app</label>
                <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  className="flex h-14 w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-[0.5em] font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  autoFocus required />
              </div>
              {error && qrData && <p className="text-sm text-destructive text-center">{error}</p>}
              <button type="submit" disabled={loading || totpCode.length !== 6}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full">
                {loading ? "Verifying..." : "Activate 2FA & Continue"}
              </button>
              <button type="button" onClick={() => { logout(); navigate("/login"); }}
                className="w-full text-center text-xs text-muted-foreground hover:underline">
                Sign out
              </button>
            </form>
          </div>
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-4">Two-factor authentication protects your account from unauthorized access</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/setup-2fa" element={isAuthenticated ? <TwoFASetupPage /> : <Navigate to="/login" replace />} />
      
      {/* Role-based landing page */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><RoleLandingPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Main dashboard - accessible to all, but click-through restricted by role */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Employee dashboard */}
      <Route path="/my-dashboard" element={
        <ProtectedRoute>
          <Layout><EmployeeDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Team Leader dashboard */}
      <Route path="/my-team" element={
        <ProtectedRoute>
          <Layout><MyTeamDashboard /></Layout>
        </ProtectedRoute>
      } />

      {/* Manager dashboard */}
      <Route path="/team" element={
        <ProtectedRoute minRole="manager">
          <Layout><ManagerDashboard /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/consultant/:name" element={
        <ProtectedRoute minRole="admin">
          <Layout><ConsultantDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Layout><Analytics /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/intelligence" element={
        <ProtectedRoute minRole="director">
          <Layout><IntelligenceDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute minRole="manager">
          <Layout><ReportBuilder /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/data-source" element={
        <ProtectedRoute minRole="admin">
          <Layout><DataSource /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/company" element={
        <ProtectedRoute minRole="admin">
          <Layout><CompanyDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/all-projects" element={
        <ProtectedRoute minRole="director">
          <Layout><DirectorProjectManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute minRole="admin">
          <Layout><AdminDashboard /></Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <MsalProvider>
            <VisibilityOverrideProvider>
            <div className="App min-h-screen bg-background">
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
              <Toaster position="top-right" />
            </div>
          </VisibilityOverrideProvider>
          </MsalProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
